import { useQuery, useMutation } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { 
  Layers, Users, Mail, FileText, Database, DollarSign, Phone, 
  Settings, ChevronRight, Eye, EyeOff, Ban, Check, X, Star, Plus, Trash2,
  Sparkles, Zap, Crown, Rocket, Gem, Building2, Briefcase, Award, Shield,
  Copy, Clock, Coins, ListChecks,
  type LucideIcon,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PlatformLayout } from "./platform-layout";
import { TierPreviewModal } from "@/components/platform/tier-preview-modal";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { PERMISSION_REGISTRY } from "@shared/permissions/registry";

type TierAudience = "individual" | "team_enterprise" | null;

interface PermissionTier {
  id: string;
  name: string;
  displayName: string | null;
  description: string | null;
  monthlyPrice: number | null;
  yearlyPrice: number | null;
  pricePerAdditionalUser: number | null;
  pricePerAdditionalTalent: number | null;
  revenueSharePercent?: string | number | null;
  permissionsJson: string;
  maxUsers: number | null;
  maxTalentProfiles: number | null;
  maxEmailsPerDay: number | null;
  maxCampaigns: number | null;
  maxLeads: number | null;
  maxEmailTemplates: number | null;
  monthlyLeadCredits?: number | null;
  signupBonusLeadCredits?: number | null;
  // Days after a billing failure before the workspace is downgraded/suspended.
  // Real column (migration 0157). Enforcement is a follow-up.
  gracePeriodDays?: number | null;
  // Feature-flag toggles + AI credit allowances (aiInitialGiftedCents /
  // aiMonthlyGiftedCents) live in this jsonb blob, not as flat columns.
  extraConfig?: Record<string, unknown> | null;
  audience?: TierAudience;
  previewBadgeText?: string | null;
  previewBadgeColor?: string | null;
  previewAccentColor?: string | null;
  previewLockedBehavior?: string | null;
  previewUpgradeCopy?: string | null;
  previewWelcomeMessage?: string | null;
  designNotes?: string | null;
  marketingIcon?: string | null;
  marketingFeaturesJson?: string | null;
  marketingHighlight?: string | null;
  marketingPopular?: boolean | null;
  marketingShowOnSignup?: boolean | null;
  // Number of workspaces currently assigned to this tier. Surfaced by
  // GET /api/platform/tiers so the Configure dialog can disable Delete (and
  // show a count badge) before the admin even clicks.
  assignedWorkspaceCount?: number;
  createdAt: string;
  updatedAt: string;
}

interface MarketingFeature {
  name: string;
  included: boolean;
  isHeader?: boolean;
  indent?: boolean;
}

// Icon picker — keep this list in sync with ICON_MAP in client/src/pages/Signup.tsx.
// Adding a new option here without registering it on Signup will fall back to Sparkles.
const MARKETING_ICONS: { key: string; icon: LucideIcon; label: string }[] = [
  { key: "Zap", icon: Zap, label: "Zap" },
  { key: "Star", icon: Star, label: "Star" },
  { key: "Users", icon: Users, label: "Users" },
  { key: "Sparkles", icon: Sparkles, label: "Sparkles" },
  { key: "Crown", icon: Crown, label: "Crown" },
  { key: "Rocket", icon: Rocket, label: "Rocket" },
  { key: "Gem", icon: Gem, label: "Gem" },
  { key: "Building2", icon: Building2, label: "Building" },
  { key: "Briefcase", icon: Briefcase, label: "Briefcase" },
  { key: "Award", icon: Award, label: "Award" },
  { key: "Shield", icon: Shield, label: "Shield" },
];

function parseMarketingFeatures(json: string | null | undefined): MarketingFeature[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (f) => f && typeof f.name === "string" && typeof f.included === "boolean"
    );
  } catch {
    return [];
  }
}

const AUDIENCE_LABELS: Record<"individual" | "team_enterprise" | "untagged", string> = {
  individual: "Individual",
  team_enterprise: "Team & Enterprise",
  untagged: "Untagged",
};

function audienceKey(audience: TierAudience | undefined): "individual" | "team_enterprise" | "untagged" {
  if (audience === "individual" || audience === "team_enterprise") return audience;
  return "untagged";
}

function AudienceBadge({ audience }: { audience: TierAudience | undefined }) {
  const k = audienceKey(audience);
  const className =
    k === "individual"
      ? "bg-blue-500/10 text-blue-700 border-blue-500/20 dark:text-blue-300"
      : k === "team_enterprise"
      ? "bg-purple-500/10 text-purple-700 border-purple-500/20 dark:text-purple-300"
      : "bg-muted text-muted-foreground border-border";
  return (
    <Badge variant="outline" className={className} data-testid={`badge-audience-${k}`}>
      {AUDIENCE_LABELS[k]}
    </Badge>
  );
}

type PermissionVisibility = "allow" | "deny" | "hide";

interface PermissionSettings {
  [key: string]: PermissionVisibility;
}

const tierOrder = ["free", "lite", "pro", "business", "agency", "enterprise", "enterprise_plus"];

const MODULE_LABELS: Record<string, string> = {
  dashboard: "Dashboard",
  leads: "Leads",
  clients: "Clients",
  invoices: "Invoices",
  schedule: "Schedule",
  approvals: "Approvals",
  communications: "Communications",
  reports: "Reports",
  admin: "Admin Panel",
  adminPanel: "Admin Panel",
  billing: "Billing",
  platform: "Platform",
  forms: "Forms",
  marketing: "Marketing",
  talent: "Talent",
  calendarHub: "Calendar Hub",
  leadGen: "Lead Gen",
  socialMedia: "Social Media",
  knowledge: "Knowledge Base",
  profiles: "Profiles",
  myEvents: "My Events",
  myProfile: "My Profile",
  managerPanel: "Manager Panel",
  enterprisePlus: "Enterprise+",
};

const VISIBILITY_OPTIONS: { value: PermissionVisibility; label: string; icon: typeof Check; color: string }[] = [
  { value: "allow", label: "Allowed", icon: Check, color: "text-green-600" },
  { value: "deny", label: "Denied", icon: Ban, color: "text-amber-600" },
  { value: "hide", label: "Hidden", icon: EyeOff, color: "text-red-600" },
];

function formatPrice(cents: number | null): string {
  if (cents === null) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

function parsePriceInput(value: string): number | null {
  if (!value) return null;
  const parsed = parseFloat(value);
  if (isNaN(parsed)) return null;
  return Math.round(parsed * 100);
}

type FeatureFlagDef = {
  key: string;
  label: string;
  description: string;
  // When set, this flag can't be enabled unless its parent is on; enabling it
  // auto-enables the parent, and turning the parent off cascades this one off.
  dependsOn?: string;
};

// All entitlement toggles, stored in permission_tiers.extra_config as booleans.
// This editor only configures them — runtime enforcement is a follow-up.
const FEATURE_FLAGS: FeatureFlagDef[] = [
  { key: "leadGen", label: "Lead Gen", description: "AI search, enrichment, email finder, Apollo." },
  { key: "aiTools", label: "AI Tools", description: "Claude assist, lead qualification, in-app chatbot." },
  { key: "massEmailWarm", label: "Mass Emails — Warm", description: "Bulk sending from a connected warm mailbox." },
  { key: "massEmailCold", label: "Mass Emails — Cold", description: "Cold bulk outreach via the dedicated relay.", dependsOn: "massEmailWarm" },
  { key: "invoicesBasic", label: "Invoices", description: "Core invoicing and public payment links." },
  { key: "invoicesAdvanced", label: "Advanced Invoices", description: "Late fees, partial payments, line-item tax." },
  { key: "lateFees", label: "Late Fees", description: "Automatic late-fee assessment on overdue invoices." },
  { key: "bookingFormsBasic", label: "Basic Booking Forms", description: "Single-step public event request forms." },
  { key: "bookingFormsAdvanced", label: "Advanced Booking Forms", description: "Multi-step forms with conditional logic." },
  { key: "commissionsGoals", label: "Commissions & Goals", description: "Sales commission tracking and goal targets." },
  { key: "reviewRequests", label: "Review Requests", description: "Automated post-event review asks." },
  { key: "scheduleBasic", label: "Master Schedule", description: "Master Schedule without Tour Planner." },
  { key: "scheduleTourPlanner", label: "Tour Planner", description: "Route optimization and tour planning.", dependsOn: "scheduleBasic" },
  { key: "coachMode", label: "Coach Mode & Aspirations", description: "Per-user life-coach layer in the voice assistant." },
  { key: "twilioSms", label: "Twilio SMS", description: "SMS campaigns and inbound SMS." },
  { key: "twilioDialer", label: "Twilio Dialer", description: "Outbound voice dialing." },
  { key: "allowsWhiteLabel", label: "Custom Branding / White Label", description: "Custom favicon, brand colors, and typography." },
  { key: "reeltimeAi", label: "Reeltime AI Connectivity", description: "Reeltime AI integration." },
  { key: "talentMode", label: "Talent Mode", description: "Talent-mode UI switch on desktop and mobile." },
  { key: "dripWarm", label: "Drip Campaigns — Warm", description: "Warm drip automation sequences." },
  { key: "dripCold", label: "Drip Campaigns — Cold", description: "Cold drip automation sequences." },
  { key: "collectionsAutomation", label: "Collections Automation", description: "Invoice payment-reminder drip." },
  { key: "aiAgentsEnabled", label: "AI Agents / Lead Outreach", description: "Guided Lead Outreach automation wizard." },
];

function emptyFeatureFlags(): Record<string, boolean> {
  return Object.fromEntries(FEATURE_FLAGS.map((f) => [f.key, false]));
}

function readFeatureFlags(extraConfig: unknown): Record<string, boolean> {
  const cfg = (extraConfig ?? {}) as Record<string, unknown>;
  const out: Record<string, boolean> = {};
  for (const f of FEATURE_FLAGS) out[f.key] = cfg[f.key] === true;
  return out;
}

function MarketingFeaturesEditor({
  features,
  onChange,
  idPrefix,
}: {
  features: MarketingFeature[];
  onChange: (next: MarketingFeature[]) => void;
  idPrefix: string;
}) {
  const update = (idx: number, patch: Partial<MarketingFeature>) => {
    const next = features.map((f, i) => (i === idx ? { ...f, ...patch } : f));
    onChange(next);
  };
  const remove = (idx: number) => onChange(features.filter((_, i) => i !== idx));
  const add = () => onChange([...features, { name: "", included: true }]);
  const move = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= features.length) return;
    const next = [...features];
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sm font-medium">Feature Bullets</Label>
          <p className="text-xs text-muted-foreground">
            Each row becomes a bullet on the Signup card. Use "Header" for section
            titles like "Everything in Pro, plus:" and "Indent" for sub-bullets.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={add}
          data-testid={`button-${idPrefix}-add-marketing-feature`}
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add Feature
        </Button>
      </div>

      {features.length === 0 ? (
        <div
          className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground"
          data-testid={`text-${idPrefix}-marketing-features-empty`}
        >
          No feature bullets yet. Add one above to start describing the tier.
        </div>
      ) : (
        <div className="space-y-2">
          {features.map((feature, idx) => (
            <div
              key={idx}
              className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto_auto_auto] gap-2 items-center rounded-md border p-2"
              data-testid={`row-${idPrefix}-marketing-feature-${idx}`}
            >
              <Input
                value={feature.name}
                onChange={(e) => update(idx, { name: e.target.value })}
                placeholder="e.g. Mass Email Campaigns"
                data-testid={`input-${idPrefix}-marketing-feature-name-${idx}`}
              />
              <label className="flex items-center gap-1 text-xs whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={feature.included}
                  onChange={(e) => update(idx, { included: e.target.checked })}
                  data-testid={`checkbox-${idPrefix}-marketing-feature-included-${idx}`}
                />
                Included
              </label>
              <label className="flex items-center gap-1 text-xs whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={!!feature.isHeader}
                  onChange={(e) => update(idx, { isHeader: e.target.checked || undefined })}
                  data-testid={`checkbox-${idPrefix}-marketing-feature-header-${idx}`}
                />
                Header
              </label>
              <label className="flex items-center gap-1 text-xs whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={!!feature.indent}
                  onChange={(e) => update(idx, { indent: e.target.checked || undefined })}
                  data-testid={`checkbox-${idPrefix}-marketing-feature-indent-${idx}`}
                />
                Indent
              </label>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                  data-testid={`button-${idPrefix}-marketing-feature-up-${idx}`}
                  title="Move up"
                >
                  ↑
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => move(idx, 1)}
                  disabled={idx === features.length - 1}
                  data-testid={`button-${idPrefix}-marketing-feature-down-${idx}`}
                  title="Move down"
                >
                  ↓
                </Button>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive"
                onClick={() => remove(idx)}
                data-testid={`button-${idPrefix}-marketing-feature-remove-${idx}`}
                title="Remove"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function TiersContent() {
  const { toast } = useToast();
  const [selectedTier, setSelectedTier] = useState<PermissionTier | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [previewTier, setPreviewTier] = useState<PermissionTier | null>(null);
  const [editValues, setEditValues] = useState<{
    displayName: string;
    description: string;
    monthlyPrice: string;
    yearlyPrice: string;
    pricePerAdditionalUser: string;
    pricePerAdditionalTalent: string;
    revenueSharePercent: string;
    maxUsers: string;
    maxTalentProfiles: string;
    maxEmailsPerDay: string;
    maxCampaigns: string;
    maxLeads: string;
    maxEmailTemplates: string;
    monthlyLeadCredits: string;
    signupBonusLeadCredits: string;
    aiInitialGiftedCents: string;
    aiMonthlyGiftedCents: string;
    gracePeriodDays: string;
    audience: "individual" | "team_enterprise" | "untagged";
    marketingIcon: string;
    marketingHighlight: string;
    marketingPopular: boolean;
    marketingShowOnSignup: boolean;
  }>({
    displayName: "",
    description: "",
    monthlyPrice: "",
    yearlyPrice: "",
    pricePerAdditionalUser: "",
    pricePerAdditionalTalent: "",
    revenueSharePercent: "",
    maxUsers: "",
    maxTalentProfiles: "",
    maxEmailsPerDay: "",
    maxCampaigns: "",
    maxLeads: "",
    maxEmailTemplates: "",
    monthlyLeadCredits: "",
    signupBonusLeadCredits: "",
    aiInitialGiftedCents: "",
    aiMonthlyGiftedCents: "",
    gracePeriodDays: "",
    audience: "untagged",
    marketingIcon: "Sparkles",
    marketingHighlight: "",
    marketingPopular: false,
    marketingShowOnSignup: false,
  });
  // Feature-flag toggles live in extraConfig (jsonb), not in the flat editValues.
  // One record keyed by flag id; populated from the tier's extraConfig when the
  // editor opens. allowsWhiteLabel / aiAgentsEnabled are now two of these flags.
  const [featureFlagsEdit, setFeatureFlagsEdit] = useState<Record<string, boolean>>(emptyFeatureFlags());
  const setFeatureFlag = (key: string, value: boolean) => {
    setFeatureFlagsEdit((prev) => {
      const next = { ...prev, [key]: value };
      if (value) {
        const def = FEATURE_FLAGS.find((f) => f.key === key);
        if (def?.dependsOn) next[def.dependsOn] = true;
      } else {
        for (const f of FEATURE_FLAGS) {
          if (f.dependsOn === key) next[f.key] = false;
        }
      }
      return next;
    });
  };
  const [editFeatures, setEditFeatures] = useState<MarketingFeature[]>([]);
  const [permissionSettings, setPermissionSettings] = useState<PermissionSettings>({});
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());

  // Add Tier dialog state — keep separate from the configure-existing dialog so the
  // two never share controlled inputs.
  const [addTierOpen, setAddTierOpen] = useState(false);
  const [newTier, setNewTier] = useState<{
    name: string;
    displayName: string;
    description: string;
    audience: "individual" | "team_enterprise" | "untagged";
    monthlyPrice: string;
    yearlyPrice: string;
    marketingIcon: string;
    marketingHighlight: string;
    marketingPopular: boolean;
    marketingShowOnSignup: boolean;
  }>({
    name: "",
    displayName: "",
    description: "",
    audience: "individual",
    monthlyPrice: "",
    yearlyPrice: "",
    marketingIcon: "Sparkles",
    marketingHighlight: "",
    marketingPopular: false,
    marketingShowOnSignup: false,
  });
  const [newTierFeatures, setNewTierFeatures] = useState<MarketingFeature[]>([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Phone billing config — separate from tier extraConfig, uses its own endpoint
  // All tiers include dialer access; these fields control per-tier pricing and allotments.
  const [phoneBillingEdit, setPhoneBillingEdit] = useState<{
    monthlyFlatCents: string;
    addLineCents: string;
    overagePerMinuteCents: string;
    overagePerSmsCents: string;
    includedMinutes: string;
    includedSms: string;
  }>({
    monthlyFlatCents: "0",
    addLineCents: "0",
    overagePerMinuteCents: "0",
    overagePerSmsCents: "0",
    includedMinutes: "0",
    includedSms: "0",
  });

  // Cold email (carousel) billing config — separate endpoint, mirrors phone billing
  const [coldEmailEdit, setColdEmailEdit] = useState<{
    coldEmailAllowed: boolean;
    includedCarousels: string;
    carouselLeaseMonthlyCents: string;
    carouselBuyMonthlyCents: string;
  }>({
    coldEmailAllowed: false,
    includedCarousels: "0",
    carouselLeaseMonthlyCents: "0",
    carouselBuyMonthlyCents: "0",
  });

  const { data: tiers, isLoading } = useQuery<PermissionTier[]>({
    queryKey: ["/api/platform/tiers"],
  });

  const updateTierMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<PermissionTier> }) => {
      return apiRequest("PATCH", `/api/platform/tiers/${id}`, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/tiers"] });
      queryClient.invalidateQueries({ queryKey: ["/api/platform/approval-requests"] });
      toast({
        title: "Tier updated",
        description: "Your changes took effect immediately.",
      });
      setSelectedTier(null);
      setEditMode(false);
    },
    onError: (error: any) => {
      toast({ 
        title: "Failed to update tier", 
        description: error.message,
        variant: "destructive" 
      });
    },
  });

  const updatePhoneBillingMutation = useMutation({
    mutationFn: async ({ tier, data }: { tier: string; data: object }) =>
      apiRequest("PUT", `/api/platform/phone-billing/${tier}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/phone-billing"] });
      toast({ title: "Phone billing saved", description: "Rates updated for this tier." });
    },
    onError: (error: any) => {
      toast({ title: "Failed to save phone billing", description: error.message, variant: "destructive" });
    },
  });

  const updateColdEmailMutation = useMutation({
    mutationFn: async ({ tier, data }: { tier: string; data: object }) =>
      apiRequest("PUT", `/api/platform/cold-email-billing/${tier}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/cold-email-billing"] });
      toast({ title: "Cold email billing saved", description: "Carousel pricing updated for this tier." });
    },
    onError: (error: any) => {
      toast({ title: "Failed to save cold email billing", description: error.message, variant: "destructive" });
    },
  });

  const openTierEditor = (tier: PermissionTier) => {
    setSelectedTier(tier);
    setEditMode(true);
    
    let existingPermissions: PermissionSettings = {};
    try {
      const parsed = tier.permissionsJson ? JSON.parse(tier.permissionsJson) : {};
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        existingPermissions = parsed as PermissionSettings;
      }
    } catch {
      existingPermissions = {};
    }
    setPermissionSettings(existingPermissions);
    
    setEditValues({
      displayName: tier.displayName || tier.name,
      description: tier.description || "",
      monthlyPrice: tier.monthlyPrice ? (tier.monthlyPrice / 100).toString() : "",
      yearlyPrice: tier.yearlyPrice ? (tier.yearlyPrice / 100).toString() : "",
      pricePerAdditionalUser: tier.pricePerAdditionalUser ? (tier.pricePerAdditionalUser / 100).toString() : "",
      pricePerAdditionalTalent: tier.pricePerAdditionalTalent ? (tier.pricePerAdditionalTalent / 100).toString() : "",
      revenueSharePercent: tier.revenueSharePercent != null ? String(tier.revenueSharePercent) : "",
      maxUsers: tier.maxUsers?.toString() || "",
      maxTalentProfiles: tier.maxTalentProfiles?.toString() || "",
      maxEmailsPerDay: tier.maxEmailsPerDay?.toString() || "",
      maxCampaigns: tier.maxCampaigns?.toString() || "",
      maxLeads: tier.maxLeads?.toString() || "",
      maxEmailTemplates: tier.maxEmailTemplates?.toString() || "",
      monthlyLeadCredits: tier.monthlyLeadCredits?.toString() || "0",
      signupBonusLeadCredits: tier.signupBonusLeadCredits?.toString() || "0",
      aiInitialGiftedCents: (((tier.extraConfig ?? {}) as Record<string, unknown>).aiInitialGiftedCents ?? 0).toString(),
      aiMonthlyGiftedCents: (((tier.extraConfig ?? {}) as Record<string, unknown>).aiMonthlyGiftedCents ?? 0).toString(),
      gracePeriodDays: tier.gracePeriodDays?.toString() || "0",
      audience: audienceKey(tier.audience),
      marketingIcon: tier.marketingIcon || "Sparkles",
      marketingHighlight: tier.marketingHighlight || "",
      marketingPopular: !!tier.marketingPopular,
      marketingShowOnSignup: !!tier.marketingShowOnSignup,
    });
    setEditFeatures(parseMarketingFeatures(tier.marketingFeaturesJson));
    // Feature flags (incl. allowsWhiteLabel / aiAgentsEnabled) are stored in
    // extraConfig, not the flat editValues.
    setFeatureFlagsEdit(readFeatureFlags(tier.extraConfig));

    // Load phone billing config for this tier slug
    fetch(`/api/platform/phone-billing/${tier.name}`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((cfg) => {
        if (cfg) {
          setPhoneBillingEdit({
            monthlyFlatCents: String(cfg.monthlyFlatCents ?? 0),
            addLineCents: String(cfg.addLineCents ?? 0),
            overagePerMinuteCents: String(cfg.overagePerMinuteCents ?? 0),
            overagePerSmsCents: String(cfg.overagePerSmsCents ?? 0),
            includedMinutes: String(cfg.includedMinutes ?? 0),
            includedSms: String(cfg.includedSms ?? 0),
          });
        } else {
          setPhoneBillingEdit({ monthlyFlatCents: "0", addLineCents: "0", overagePerMinuteCents: "0", overagePerSmsCents: "0", includedMinutes: "0", includedSms: "0" });
        }
      })
      .catch(() => {});

    // Load cold email (carousel) billing config for this tier slug
    fetch(`/api/platform/cold-email-billing/${tier.name}`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((cfg) => {
        if (cfg) {
          setColdEmailEdit({
            coldEmailAllowed: !!cfg.coldEmailAllowed,
            includedCarousels: String(cfg.includedCarousels ?? 0),
            carouselLeaseMonthlyCents: String(cfg.carouselLeaseMonthlyCents ?? 0),
            carouselBuyMonthlyCents: String(cfg.carouselBuyMonthlyCents ?? 0),
          });
        } else {
          setColdEmailEdit({ coldEmailAllowed: false, includedCarousels: "0", carouselLeaseMonthlyCents: "0", carouselBuyMonthlyCents: "0" });
        }
      })
      .catch(() => {});

    setExpandedModules(new Set());
  };

  const handleSave = () => {
    if (!selectedTier) return;
    
    // Strip empty feature rows so blanks the admin started typing don't end up
    // on the public Signup card.
    const cleanedFeatures = editFeatures
      .map((f) => ({ ...f, name: f.name.trim() }))
      .filter((f) => f.name.length > 0);

    // Feature flags + AI credit allowances are merged into extraConfig by the
    // PATCH handler; the flat columns (gracePeriodDays, monthlyLeadCredits, …)
    // are written directly.
    const extraConfig: Record<string, unknown> = {
      ...featureFlagsEdit,
      aiInitialGiftedCents: editValues.aiInitialGiftedCents ? parseInt(editValues.aiInitialGiftedCents) : 0,
      aiMonthlyGiftedCents: editValues.aiMonthlyGiftedCents ? parseInt(editValues.aiMonthlyGiftedCents) : 0,
    };

    updateTierMutation.mutate({
      id: selectedTier.id,
      updates: {
        displayName: editValues.displayName || null,
        description: editValues.description || null,
        monthlyPrice: parsePriceInput(editValues.monthlyPrice),
        yearlyPrice: parsePriceInput(editValues.yearlyPrice),
        pricePerAdditionalUser: parsePriceInput(editValues.pricePerAdditionalUser),
        pricePerAdditionalTalent: parsePriceInput(editValues.pricePerAdditionalTalent),
        revenueSharePercent: editValues.revenueSharePercent !== "" ? parseFloat(editValues.revenueSharePercent) : null,
        maxUsers: editValues.maxUsers ? parseInt(editValues.maxUsers) : null,
        maxTalentProfiles: editValues.maxTalentProfiles ? parseInt(editValues.maxTalentProfiles) : null,
        maxEmailsPerDay: editValues.maxEmailsPerDay ? parseInt(editValues.maxEmailsPerDay) : null,
        maxCampaigns: editValues.maxCampaigns ? parseInt(editValues.maxCampaigns) : null,
        maxLeads: editValues.maxLeads ? parseInt(editValues.maxLeads) : null,
        maxEmailTemplates: editValues.maxEmailTemplates ? parseInt(editValues.maxEmailTemplates) : null,
        monthlyLeadCredits: editValues.monthlyLeadCredits ? parseInt(editValues.monthlyLeadCredits) : 0,
        signupBonusLeadCredits: editValues.signupBonusLeadCredits ? parseInt(editValues.signupBonusLeadCredits) : 0,
        gracePeriodDays: editValues.gracePeriodDays ? parseInt(editValues.gracePeriodDays) : 0,
        audience: editValues.audience === "untagged" ? null : editValues.audience,
        permissionsJson: JSON.stringify(permissionSettings),
        marketingIcon: editValues.marketingIcon || null,
        marketingHighlight: editValues.marketingHighlight.trim() || null,
        marketingPopular: editValues.marketingPopular,
        marketingShowOnSignup: editValues.marketingShowOnSignup,
        marketingFeatures: cleanedFeatures,
        extraConfig,
      } as Partial<PermissionTier>,
    });

    // Phone billing config — separate PUT to the dedicated endpoint
    updatePhoneBillingMutation.mutate({
      tier: selectedTier.name,
      data: {
        phoneAllowed: true,
        dialerIncluded: true,
        monthlyFlatCents: parseInt(phoneBillingEdit.monthlyFlatCents) || 0,
        addLineCents: parseInt(phoneBillingEdit.addLineCents) || 0,
        overagePerMinuteCents: parseInt(phoneBillingEdit.overagePerMinuteCents) || 0,
        overagePerSmsCents: parseInt(phoneBillingEdit.overagePerSmsCents) || 0,
        includedMinutes: parseInt(phoneBillingEdit.includedMinutes) || 0,
        includedSms: parseInt(phoneBillingEdit.includedSms) || 0,
      },
    });

    // Cold email (carousel) billing config — separate PUT to the dedicated endpoint
    updateColdEmailMutation.mutate({
      tier: selectedTier.name,
      data: {
        coldEmailAllowed: coldEmailEdit.coldEmailAllowed,
        includedCarousels: parseInt(coldEmailEdit.includedCarousels) || 0,
        carouselLeaseMonthlyCents: parseInt(coldEmailEdit.carouselLeaseMonthlyCents) || 0,
        carouselBuyMonthlyCents: parseInt(coldEmailEdit.carouselBuyMonthlyCents) || 0,
      },
    });
  };

  const createTierMutation = useMutation({
    mutationFn: async (body: {
      name: string;
      displayName: string | null;
      description: string | null;
      audience: "individual" | "team_enterprise" | null;
      monthlyPrice: number | null;
      yearlyPrice: number | null;
      marketingIcon: string | null;
      marketingFeatures: MarketingFeature[];
      marketingHighlight: string | null;
      marketingPopular: boolean;
      marketingShowOnSignup: boolean;
    }) => {
      return apiRequest("POST", "/api/platform/tiers", body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/tiers"] });
      queryClient.invalidateQueries({ queryKey: ["/api/platform/approval-requests"] });
      toast({
        title: "Tier created",
        description: "The new tier was created immediately.",
      });
      setAddTierOpen(false);
      setNewTier({
        name: "",
        displayName: "",
        description: "",
        audience: "individual",
        monthlyPrice: "",
        yearlyPrice: "",
        marketingIcon: "Sparkles",
        marketingHighlight: "",
        marketingPopular: false,
        marketingShowOnSignup: false,
      });
      setNewTierFeatures([]);
    },
    onError: (error: any) => {
      toast({
        title: "Failed to create tier",
        description: error?.message || "Please check the form and try again.",
        variant: "destructive",
      });
    },
  });

  const deleteTierMutation = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/platform/tiers/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/tiers"] });
      queryClient.invalidateQueries({ queryKey: ["/api/platform/approval-requests"] });
      toast({
        title: "Tier deleted",
        description: "The tier was deleted immediately.",
      });
      setDeleteConfirmOpen(false);
      setSelectedTier(null);
      setEditMode(false);
    },
    onError: (error: any) => {
      toast({
        title: "Failed to delete tier",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
      setDeleteConfirmOpen(false);
    },
  });

  const duplicateTierMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/platform/tiers/${id}/duplicate`);
      return (await res.json()) as PermissionTier;
    },
    onSuccess: async (clone) => {
      await queryClient.invalidateQueries({ queryKey: ["/api/platform/tiers"] });
      toast({
        title: "Tier duplicated",
        description: `Created "${clone.displayName || clone.name}". Configure it below.`,
      });
      // Open the Configure dialog on the freshly created clone.
      openTierEditor(clone);
    },
    onError: (error: any) => {
      toast({
        title: "Failed to duplicate tier",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleCreateTier = () => {
    const slug = newTier.name.trim().toLowerCase();
    if (!slug || !/^[a-z0-9_]+$/.test(slug)) {
      toast({
        title: "Invalid slug",
        description: "Use lowercase letters, numbers, and underscores only.",
        variant: "destructive",
      });
      return;
    }
    if (!newTier.displayName.trim()) {
      toast({
        title: "Display name required",
        description: "Give the tier a friendly display name.",
        variant: "destructive",
      });
      return;
    }
    const cleanedFeatures = newTierFeatures
      .map((f) => ({ ...f, name: f.name.trim() }))
      .filter((f) => f.name.length > 0);

    createTierMutation.mutate({
      name: slug,
      displayName: newTier.displayName.trim(),
      description: newTier.description.trim() || null,
      audience: newTier.audience === "untagged" ? null : newTier.audience,
      monthlyPrice: parsePriceInput(newTier.monthlyPrice),
      yearlyPrice: parsePriceInput(newTier.yearlyPrice),
      marketingIcon: newTier.marketingIcon || null,
      marketingFeatures: cleanedFeatures,
      marketingHighlight: newTier.marketingHighlight.trim() || null,
      marketingPopular: newTier.marketingPopular,
      marketingShowOnSignup: newTier.marketingShowOnSignup,
    });
  };

  const setPermissionVisibility = (key: string, visibility: PermissionVisibility) => {
    setPermissionSettings(prev => {
      const updated = { ...prev };
      if (visibility === "allow") {
        delete updated[key];
      } else {
        updated[key] = visibility;
      }
      return updated;
    });
  };

  const setBulkVisibility = (module: string, visibility: PermissionVisibility) => {
    const modulePermissions = PERMISSION_REGISTRY.filter(p => p.module === module);
    setPermissionSettings(prev => {
      const updated = { ...prev };
      modulePermissions.forEach(p => {
        if (visibility === "allow") {
          delete updated[p.key];
        } else {
          updated[p.key] = visibility;
        }
      });
      return updated;
    });
  };

  const getModuleVisibilitySummary = (module: string): { allowed: number; denied: number; hidden: number } => {
    const modulePermissions = PERMISSION_REGISTRY.filter(p => p.module === module);
    let allowed = 0, denied = 0, hidden = 0;
    modulePermissions.forEach(p => {
      const visibility = permissionSettings[p.key] || "allow";
      if (visibility === "allow") allowed++;
      else if (visibility === "deny") denied++;
      else if (visibility === "hide") hidden++;
    });
    return { allowed, denied, hidden };
  };

  const toggleModule = (module: string) => {
    setExpandedModules(prev => {
      const next = new Set(prev);
      if (next.has(module)) {
        next.delete(module);
      } else {
        next.add(module);
      }
      return next;
    });
  };

  const sortedTiers = tiers
    ? [...tiers].sort((a, b) => {
        const ai = tierOrder.indexOf(a.name);
        const bi = tierOrder.indexOf(b.name);
        const aNorm = ai === -1 ? tierOrder.length : ai;
        const bNorm = bi === -1 ? tierOrder.length : bi;
        return aNorm - bNorm;
      })
    : undefined;

  const modules = [...new Set(PERMISSION_REGISTRY.map(p => p.module))];

  return (
    <>
      <div className="p-3 sm:p-6 space-y-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Permission Tiers</h1>
            <p className="text-muted-foreground">
              Configure limits, pricing, and feature visibility for each subscription tier
            </p>
          </div>
          <Button
            onClick={() => setAddTierOpen(true)}
            data-testid="button-add-tier"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Tier
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
          </div>
        ) : (
          <div className="grid gap-6">
            {sortedTiers?.map((tier) => {
              let permissions: PermissionSettings = {};
              try {
                permissions = tier.permissionsJson ? JSON.parse(tier.permissionsJson) : {};
                if (typeof permissions !== "object" || permissions === null || Array.isArray(permissions)) {
                  permissions = {};
                }
              } catch {
                permissions = {};
              }
              const hiddenCount = Object.values(permissions).filter(v => v === "hide").length;
              const deniedCount = Object.values(permissions).filter(v => v === "deny").length;
              const workspaceCount = tier.assignedWorkspaceCount ?? 0;

              return (
                <Card
                  key={tier.id}
                  data-testid={`tier-card-${tier.name}`}
                  onDoubleClick={() => setPreviewTier(tier)}
                  className="cursor-pointer hover-elevate"
                  title="Double-click to open Tier Preview & Settings"
                >
                  <CardHeader>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-3">
                        <Layers className="h-5 w-5 text-red-600" />
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <CardTitle className="capitalize">
                              {tier.displayName || tier.name}
                            </CardTitle>
                            <AudienceBadge audience={tier.audience} />
                          </div>
                          <CardDescription>{tier.description}</CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="flex flex-wrap gap-2">
                          {workspaceCount > 0 ? (
                            <Badge
                              variant="secondary"
                              className="gap-1"
                              data-testid={`badge-workspace-count-${tier.name}`}
                            >
                              <Building2 className="h-3 w-3" />
                              {workspaceCount === 1
                                ? "1 workspace"
                                : `${workspaceCount} workspaces`}
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className="gap-1 text-muted-foreground"
                              data-testid={`badge-workspace-count-${tier.name}`}
                            >
                              <Building2 className="h-3 w-3" />
                              No workspaces
                            </Badge>
                          )}
                          {hiddenCount > 0 && (
                            <Badge variant="secondary" className="gap-1">
                              <EyeOff className="h-3 w-3" />
                              {hiddenCount} hidden
                            </Badge>
                          )}
                          {deniedCount > 0 && (
                            <Badge variant="outline" className="gap-1">
                              <Ban className="h-3 w-3" />
                              {deniedCount} denied
                            </Badge>
                          )}
                          {(() => {
                            const cfg = (tier.extraConfig ?? {}) as Record<string, unknown>;
                            const featureCount = FEATURE_FLAGS.filter((f) => cfg[f.key] === true).length;
                            return (
                              <Badge
                                variant="secondary"
                                className="gap-1"
                                data-testid={`badge-feature-count-${tier.name}`}
                              >
                                <ListChecks className="h-3 w-3" />
                                {featureCount} features on
                              </Badge>
                            );
                          })()}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); setPreviewTier(tier); }}
                          data-testid={`button-preview-tier-${tier.name}`}
                          title="Open visual preview & per-tier UI config"
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Preview
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); duplicateTierMutation.mutate(tier.id); }}
                          disabled={duplicateTierMutation.isPending}
                          data-testid={`button-duplicate-tier-${tier.name}`}
                          title="Create an editable copy of this tier"
                        >
                          <Copy className="h-4 w-4 mr-2" />
                          Duplicate
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); openTierEditor(tier); }}
                          data-testid={`button-edit-tier-${tier.name}`}
                        >
                          <Settings className="h-4 w-4 mr-2" />
                          Configure
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-7 gap-4">
                      <div className="space-y-1">
                        <Label className="flex items-center gap-2 text-sm text-muted-foreground">
                          <DollarSign className="h-4 w-4" />
                          Monthly
                        </Label>
                        <div className="font-medium">
                          {formatPrice(tier.monthlyPrice)}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="flex items-center gap-2 text-sm text-muted-foreground">
                          <DollarSign className="h-4 w-4" />
                          Yearly
                        </Label>
                        <div className="font-medium">
                          {formatPrice(tier.yearlyPrice)}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="flex items-center gap-2 text-sm text-muted-foreground">
                          <DollarSign className="h-4 w-4" />
                          Per User
                        </Label>
                        <div className="font-medium">
                          {tier.pricePerAdditionalUser ? `${formatPrice(tier.pricePerAdditionalUser)}/mo` : "—"}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Users className="h-4 w-4" />
                          Max Users
                        </Label>
                        <div className="font-medium">
                          {tier.maxUsers ?? "Unlimited"}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Star className="h-4 w-4" />
                          Talent
                        </Label>
                        <div className="font-medium">
                          {tier.maxTalentProfiles ?? "Unlimited"}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Mail className="h-4 w-4" />
                          Emails/Day
                        </Label>
                        <div className="font-medium">
                          {tier.maxEmailsPerDay ?? "Unlimited"}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="flex items-center gap-2 text-sm text-muted-foreground">
                          <FileText className="h-4 w-4" />
                          Campaigns
                        </Label>
                        <div className="font-medium">
                          {tier.maxCampaigns ?? "Unlimited"}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Database className="h-4 w-4" />
                          Leads
                        </Label>
                        <div className="font-medium">
                          {tier.maxLeads ?? "Unlimited"}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="flex items-center gap-2 text-sm text-muted-foreground">
                          <FileText className="h-4 w-4" />
                          Templates
                        </Label>
                        <div className="font-medium">
                          {tier.maxEmailTemplates ?? "Unlimited"}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={editMode} onOpenChange={(open) => !open && setEditMode(false)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="capitalize">
              Configure {selectedTier?.displayName || selectedTier?.name} Tier
            </DialogTitle>
            <DialogDescription>
              Edit tier settings, pricing, limits, and feature visibility
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="settings" className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="w-full justify-start">
              <TabsTrigger value="settings" data-testid="tab-tier-settings">
                Settings & Limits
              </TabsTrigger>
              <TabsTrigger value="marketing" data-testid="tab-tier-marketing">
                Signup Marketing
              </TabsTrigger>
              <TabsTrigger value="feature-flags" data-testid="tab-tier-feature-flags">
                Feature Flags
              </TabsTrigger>
              <TabsTrigger value="permissions" data-testid="tab-tier-permissions">
                Feature Visibility
              </TabsTrigger>
            </TabsList>

            <TabsContent value="settings" className="flex-1 overflow-auto mt-4 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="displayName">Display Name</Label>
                  <Input
                    id="displayName"
                    value={editValues.displayName}
                    onChange={(e) => setEditValues(prev => ({ ...prev, displayName: e.target.value }))}
                    placeholder="Enter display name"
                    data-testid="input-tier-display-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="audience">Audience</Label>
                  <Select
                    value={editValues.audience}
                    onValueChange={(value) =>
                      setEditValues((prev) => ({
                        ...prev,
                        audience: value as "individual" | "team_enterprise" | "untagged",
                      }))
                    }
                  >
                    <SelectTrigger id="audience" data-testid="select-tier-audience">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">Individual</SelectItem>
                      <SelectItem value="team_enterprise">Team and Enterprise</SelectItem>
                      <SelectItem value="untagged">Untagged (shows under both)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Controls which tab on the public Signup page shows this tier.
                  </p>
                </div>
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={editValues.description}
                    onChange={(e) => setEditValues(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Enter description"
                    data-testid="input-tier-description"
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-medium mb-4">Pricing</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="monthlyPrice">Monthly Price ($)</Label>
                    <Input
                      id="monthlyPrice"
                      type="number"
                      step="0.01"
                      value={editValues.monthlyPrice}
                      onChange={(e) => setEditValues(prev => ({ ...prev, monthlyPrice: e.target.value }))}
                      placeholder="0.00"
                      data-testid="input-tier-monthly-price"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="yearlyPrice">Yearly Price ($)</Label>
                    <Input
                      id="yearlyPrice"
                      type="number"
                      step="0.01"
                      value={editValues.yearlyPrice}
                      onChange={(e) => setEditValues(prev => ({ ...prev, yearlyPrice: e.target.value }))}
                      placeholder="0.00"
                      data-testid="input-tier-yearly-price"
                    />
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pricePerAdditionalUser">Price Per Additional User ($/month)</Label>
                    <Input
                      id="pricePerAdditionalUser"
                      type="number"
                      step="0.01"
                      value={editValues.pricePerAdditionalUser}
                      onChange={(e) => setEditValues(prev => ({ ...prev, pricePerAdditionalUser: e.target.value }))}
                      placeholder="0.00"
                      data-testid="input-tier-price-per-user"
                    />
                    <p className="text-xs text-muted-foreground">
                      Charge this amount per user beyond the base user limit. Auto-calculated during checkout.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pricePerAdditionalTalent">Price Per Additional Talent Profile ($/month)</Label>
                    <Input
                      id="pricePerAdditionalTalent"
                      type="number"
                      step="0.01"
                      value={editValues.pricePerAdditionalTalent}
                      onChange={(e) => setEditValues(prev => ({ ...prev, pricePerAdditionalTalent: e.target.value }))}
                      placeholder="0.00"
                      data-testid="input-tier-price-per-talent"
                    />
                    <p className="text-xs text-muted-foreground">
                      Charge this amount per talent profile beyond the base limit. Used by the talent profile add-on.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="revenueSharePercent">Revenue Share (%)</Label>
                    <div className="relative">
                      <Input
                        id="revenueSharePercent"
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={editValues.revenueSharePercent}
                        onChange={(e) => setEditValues(prev => ({ ...prev, revenueSharePercent: e.target.value }))}
                        placeholder="e.g. 1.5"
                        className="pr-7"
                        data-testid="input-tier-revenue-share"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Monthly charge as % of tracked invoice revenue. Overrides the platform default. Leave blank to use the platform default.
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-medium mb-4">Usage Limits</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="maxUsers">Max Users</Label>
                    <Input
                      id="maxUsers"
                      type="number"
                      value={editValues.maxUsers}
                      onChange={(e) => setEditValues(prev => ({ ...prev, maxUsers: e.target.value }))}
                      placeholder="Unlimited"
                      data-testid="input-tier-max-users"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="maxTalentProfiles">Max Talent</Label>
                    <Input
                      id="maxTalentProfiles"
                      type="number"
                      value={editValues.maxTalentProfiles}
                      onChange={(e) => setEditValues(prev => ({ ...prev, maxTalentProfiles: e.target.value }))}
                      placeholder="Unlimited"
                      data-testid="input-tier-max-talent"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="maxEmailsPerDay">Emails/Day</Label>
                    <Input
                      id="maxEmailsPerDay"
                      type="number"
                      value={editValues.maxEmailsPerDay}
                      onChange={(e) => setEditValues(prev => ({ ...prev, maxEmailsPerDay: e.target.value }))}
                      placeholder="Unlimited"
                      data-testid="input-tier-max-emails"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="maxCampaigns">Max Campaigns</Label>
                    <Input
                      id="maxCampaigns"
                      type="number"
                      value={editValues.maxCampaigns}
                      onChange={(e) => setEditValues(prev => ({ ...prev, maxCampaigns: e.target.value }))}
                      placeholder="Unlimited"
                      data-testid="input-tier-max-campaigns"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="maxLeads">Max Leads</Label>
                    <Input
                      id="maxLeads"
                      type="number"
                      value={editValues.maxLeads}
                      onChange={(e) => setEditValues(prev => ({ ...prev, maxLeads: e.target.value }))}
                      placeholder="Unlimited"
                      data-testid="input-tier-max-leads"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="maxEmailTemplates">Max Templates</Label>
                    <Input
                      id="maxEmailTemplates"
                      type="number"
                      value={editValues.maxEmailTemplates}
                      onChange={(e) => setEditValues(prev => ({ ...prev, maxEmailTemplates: e.target.value }))}
                      placeholder="Unlimited"
                      data-testid="input-tier-max-email-templates"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <Coins className="h-4 w-4 text-muted-foreground" />
                  Credits & Allowances
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="monthlyLeadCredits">Monthly Lead Credits</Label>
                    <Input
                      id="monthlyLeadCredits"
                      type="number"
                      min="0"
                      value={editValues.monthlyLeadCredits}
                      onChange={(e) => setEditValues(prev => ({ ...prev, monthlyLeadCredits: e.target.value }))}
                      placeholder="0"
                      data-testid="input-tier-monthly-lead-credits"
                    />
                    <p className="text-xs text-muted-foreground">
                      Use-it-or-lose-it Lead Gen credits granted each month. Resets on the 1st.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signupBonusLeadCredits">Sign-on Bonus Credits</Label>
                    <Input
                      id="signupBonusLeadCredits"
                      type="number"
                      min="0"
                      value={editValues.signupBonusLeadCredits}
                      onChange={(e) => setEditValues(prev => ({ ...prev, signupBonusLeadCredits: e.target.value }))}
                      placeholder="0"
                      data-testid="input-tier-signup-bonus-lead-credits"
                    />
                    <p className="text-xs text-muted-foreground">
                      One-time bonus credits awarded on signup. Lands in the rollover bucket.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="aiInitialGiftedCents">Starting AI Credits (cents)</Label>
                    <Input
                      id="aiInitialGiftedCents"
                      type="number"
                      min="0"
                      value={editValues.aiInitialGiftedCents}
                      onChange={(e) => setEditValues(prev => ({ ...prev, aiInitialGiftedCents: e.target.value }))}
                      placeholder="0"
                      data-testid="input-tier-ai-initial-gifted-cents"
                    />
                    <p className="text-xs text-muted-foreground">
                      One-time AI credit balance (in cents) gifted when a workspace signs up on this tier.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="aiMonthlyGiftedCents">Monthly AI Credits (cents)</Label>
                    <Input
                      id="aiMonthlyGiftedCents"
                      type="number"
                      min="0"
                      value={editValues.aiMonthlyGiftedCents}
                      onChange={(e) => setEditValues(prev => ({ ...prev, aiMonthlyGiftedCents: e.target.value }))}
                      placeholder="0"
                      data-testid="input-tier-ai-monthly-gifted-cents"
                    />
                    <p className="text-xs text-muted-foreground">
                      Recurring monthly AI credit allowance (in cents) for workspaces on this tier.
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  Billing
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="gracePeriodDays">Grace Period (days)</Label>
                    <Input
                      id="gracePeriodDays"
                      type="number"
                      min="0"
                      value={editValues.gracePeriodDays}
                      onChange={(e) => setEditValues(prev => ({ ...prev, gracePeriodDays: e.target.value }))}
                      placeholder="0"
                      data-testid="input-tier-grace-period-days"
                    />
                    <p className="text-xs text-muted-foreground">
                      Days after a billing failure before the workspace is downgraded or suspended. 0 = immediate.
                    </p>
                  </div>
                </div>
              </div>

              {/* Phone Billing */}
              <div className="border-t pt-4">
                <h3 className="font-medium mb-2 flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  Business Phone Billing
                </h3>
                <p className="text-xs text-muted-foreground mb-4">
                  Dialer is available on all tiers. Set per-tier pricing and monthly allotments below.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phoneFlatCents">Cost per line (cents/mo)</Label>
                    <Input
                      id="phoneFlatCents"
                      type="number"
                      min="0"
                      value={phoneBillingEdit.monthlyFlatCents}
                      onChange={(e) => setPhoneBillingEdit(prev => ({ ...prev, monthlyFlatCents: e.target.value }))}
                      placeholder="0"
                      data-testid="input-tier-phone-flat-cents"
                    />
                    <p className="text-xs text-muted-foreground">e.g. 2900 = $29.00/mo per line</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phoneAddLineCents">Add a line (cents/mo)</Label>
                    <Input
                      id="phoneAddLineCents"
                      type="number"
                      min="0"
                      value={phoneBillingEdit.addLineCents}
                      onChange={(e) => setPhoneBillingEdit(prev => ({ ...prev, addLineCents: e.target.value }))}
                      placeholder="0"
                      data-testid="input-tier-phone-add-line-cents"
                    />
                    <p className="text-xs text-muted-foreground">Cost per additional line/mo</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phoneOverageMin">Cost per minute (cents)</Label>
                    <Input
                      id="phoneOverageMin"
                      type="number"
                      min="0"
                      value={phoneBillingEdit.overagePerMinuteCents}
                      onChange={(e) => setPhoneBillingEdit(prev => ({ ...prev, overagePerMinuteCents: e.target.value }))}
                      placeholder="2"
                      data-testid="input-tier-phone-overage-per-minute"
                    />
                    <p className="text-xs text-muted-foreground">e.g. 2 = $0.02/min</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phoneOverageSms">Cost per text (cents)</Label>
                    <Input
                      id="phoneOverageSms"
                      type="number"
                      min="0"
                      value={phoneBillingEdit.overagePerSmsCents}
                      onChange={(e) => setPhoneBillingEdit(prev => ({ ...prev, overagePerSmsCents: e.target.value }))}
                      placeholder="1"
                      data-testid="input-tier-phone-overage-per-sms"
                    />
                    <p className="text-xs text-muted-foreground">e.g. 1 = $0.01/text</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phoneIncludedMin">Included minutes/mo</Label>
                    <Input
                      id="phoneIncludedMin"
                      type="number"
                      min="0"
                      value={phoneBillingEdit.includedMinutes}
                      onChange={(e) => setPhoneBillingEdit(prev => ({ ...prev, includedMinutes: e.target.value }))}
                      placeholder="0"
                      data-testid="input-tier-phone-included-minutes"
                    />
                    <p className="text-xs text-muted-foreground">0 = pay-as-you-go</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phoneIncludedSms">Included texts/mo</Label>
                    <Input
                      id="phoneIncludedSms"
                      type="number"
                      min="0"
                      value={phoneBillingEdit.includedSms}
                      onChange={(e) => setPhoneBillingEdit(prev => ({ ...prev, includedSms: e.target.value }))}
                      placeholder="0"
                      data-testid="input-tier-phone-included-sms"
                    />
                    <p className="text-xs text-muted-foreground">0 = pay-as-you-go</p>
                  </div>
                </div>
              </div>

              {/* Cold Email (Carousel) Billing */}
              <div className="border-t pt-4">
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  Cold Email Carousel Billing
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between rounded-lg border p-3">
                    <div>
                      <div className="text-sm font-medium">Cold email allowed on this tier</div>
                      <div className="text-xs text-muted-foreground">Workspaces can lease/buy cold sending carousels (domains + mailboxes)</div>
                    </div>
                    <Switch
                      checked={coldEmailEdit.coldEmailAllowed}
                      onCheckedChange={(v) => setColdEmailEdit(prev => ({ ...prev, coldEmailAllowed: v }))}
                      data-testid="switch-tier-cold-email-allowed"
                    />
                  </div>
                  {coldEmailEdit.coldEmailAllowed && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="coldIncludedCarousels">Included carousels/mo</Label>
                        <Input
                          id="coldIncludedCarousels"
                          type="number"
                          min="0"
                          value={coldEmailEdit.includedCarousels}
                          onChange={(e) => setColdEmailEdit(prev => ({ ...prev, includedCarousels: e.target.value }))}
                          placeholder="0"
                          data-testid="input-tier-cold-included-carousels"
                        />
                        <p className="text-xs text-muted-foreground">Bundled at this tier — upgrading auto-cancels paid carousel add-ons up to this count</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="coldLeaseCents">Carousel lease price (cents/mo)</Label>
                        <Input
                          id="coldLeaseCents"
                          type="number"
                          min="0"
                          value={coldEmailEdit.carouselLeaseMonthlyCents}
                          onChange={(e) => setColdEmailEdit(prev => ({ ...prev, carouselLeaseMonthlyCents: e.target.value }))}
                          placeholder="2000"
                          data-testid="input-tier-cold-lease-cents"
                        />
                        <p className="text-xs text-muted-foreground">e.g. 2000 = $20.00/mo</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="coldBuyCents">Carousel buy price (cents/mo)</Label>
                        <Input
                          id="coldBuyCents"
                          type="number"
                          min="0"
                          value={coldEmailEdit.carouselBuyMonthlyCents}
                          onChange={(e) => setColdEmailEdit(prev => ({ ...prev, carouselBuyMonthlyCents: e.target.value }))}
                          placeholder="3500"
                          data-testid="input-tier-cold-buy-cents"
                        />
                        <p className="text-xs text-muted-foreground">e.g. 3500 = $35.00/mo</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="marketing" className="flex-1 overflow-auto mt-4 space-y-6">
              <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                These fields drive the public Signup page card for this tier. Toggle
                <strong className="mx-1">Show on Signup</strong>
                once the copy is ready — the audience tab is taken from
                <strong className="mx-1">Settings &amp; Limits → Audience</strong>.
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="marketingIcon">Icon</Label>
                  <Select
                    value={editValues.marketingIcon}
                    onValueChange={(value) =>
                      setEditValues((prev) => ({ ...prev, marketingIcon: value }))
                    }
                  >
                    <SelectTrigger id="marketingIcon" data-testid="select-tier-marketing-icon">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MARKETING_ICONS.map((opt) => (
                        <SelectItem key={opt.key} value={opt.key}>
                          <div className="flex items-center gap-2">
                            <opt.icon className="h-4 w-4" />
                            {opt.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Shown next to the tier name on the Signup card.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="marketingHighlight">Highlight Badge</Label>
                  <Input
                    id="marketingHighlight"
                    value={editValues.marketingHighlight}
                    onChange={(e) =>
                      setEditValues((prev) => ({ ...prev, marketingHighlight: e.target.value }))
                    }
                    placeholder="e.g. Most Popular, Best Value"
                    maxLength={50}
                    data-testid="input-tier-marketing-highlight"
                  />
                  <p className="text-xs text-muted-foreground">
                    Leave blank for no badge above the card.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center justify-between rounded-md border p-3">
                  <div>
                    <Label htmlFor="marketingPopular" className="text-sm font-medium">
                      Popular Styling
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Applies the gradient/lifted treatment used for the "Most Popular" tier.
                    </p>
                  </div>
                  <Switch
                    id="marketingPopular"
                    checked={editValues.marketingPopular}
                    onCheckedChange={(checked) =>
                      setEditValues((prev) => ({ ...prev, marketingPopular: checked }))
                    }
                    data-testid="switch-tier-marketing-popular"
                  />
                </div>
                <div className="flex items-center justify-between rounded-md border p-3">
                  <div>
                    <Label htmlFor="marketingShowOnSignup" className="text-sm font-medium">
                      Show on Signup
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Hidden tiers stay invisible on the public Signup page even if marketing copy exists.
                    </p>
                  </div>
                  <Switch
                    id="marketingShowOnSignup"
                    checked={editValues.marketingShowOnSignup}
                    onCheckedChange={(checked) =>
                      setEditValues((prev) => ({ ...prev, marketingShowOnSignup: checked }))
                    }
                    data-testid="switch-tier-marketing-show"
                  />
                </div>
              </div>

              <MarketingFeaturesEditor
                features={editFeatures}
                onChange={setEditFeatures}
                idPrefix="edit"
              />
            </TabsContent>

            <TabsContent value="permissions" className="flex-1 overflow-hidden mt-4 flex flex-col">
              <div className="mb-4 p-3 bg-muted rounded-lg shrink-0">
                <p className="text-sm text-muted-foreground">
                  <strong>Allowed:</strong> Feature is available. 
                  <strong className="ml-2">Denied:</strong> Feature is disabled but visible in settings. 
                  <strong className="ml-2">Hidden:</strong> Feature is completely hidden from workspace admins.
                </p>
              </div>
              
              <ScrollArea className="flex-1 h-[400px]">
                <div className="space-y-2 pr-4">
                  {modules.map((module) => {
                    const modulePermissions = PERMISSION_REGISTRY.filter(p => p.module === module).sort((a, b) => a.sortOrder - b.sortOrder);
                    const summary = getModuleVisibilitySummary(module);
                    const isExpanded = expandedModules.has(module);
                    
                    return (
                      <Collapsible key={module} open={isExpanded} onOpenChange={() => toggleModule(module)}>
                        <div className="border rounded-lg">
                          <CollapsibleTrigger asChild>
                            <div 
                              className="flex items-center justify-between p-3 cursor-pointer hover-elevate"
                              data-testid={`module-toggle-${module}`}
                            >
                              <div className="flex items-center gap-3">
                                <ChevronRight className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                <span className="font-medium">{MODULE_LABELS[module] || module}</span>
                                <div className="flex gap-1">
                                  {summary.allowed > 0 && (
                                    <Badge variant="secondary" className="text-xs gap-1">
                                      <Check className="h-3 w-3 text-green-600" />
                                      {summary.allowed}
                                    </Badge>
                                  )}
                                  {summary.denied > 0 && (
                                    <Badge variant="outline" className="text-xs gap-1">
                                      <Ban className="h-3 w-3 text-amber-600" />
                                      {summary.denied}
                                    </Badge>
                                  )}
                                  {summary.hidden > 0 && (
                                    <Badge variant="destructive" className="text-xs gap-1">
                                      <EyeOff className="h-3 w-3" />
                                      {summary.hidden}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <Select
                                value=""
                                onValueChange={(value) => setBulkVisibility(module, value as PermissionVisibility)}
                              >
                                <SelectTrigger 
                                  className="w-[140px]" 
                                  onClick={(e) => e.stopPropagation()}
                                  data-testid={`bulk-action-${module}`}
                                >
                                  <SelectValue placeholder="Bulk actions..." />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="allow">Set All Allowed</SelectItem>
                                  <SelectItem value="deny">Set All Denied</SelectItem>
                                  <SelectItem value="hide">Set All Hidden</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </CollapsibleTrigger>
                          
                          <CollapsibleContent>
                            <div className="border-t">
                              {modulePermissions.map((permission) => {
                                const visibility = permissionSettings[permission.key] || "allow";
                                
                                return (
                                  <div 
                                    key={permission.key}
                                    className="flex items-center justify-between p-3 pl-10 border-b last:border-b-0"
                                    data-testid={`permission-row-${permission.key}`}
                                  >
                                    <div>
                                      <div className="font-medium text-sm">{permission.label}</div>
                                      <div className="text-xs text-muted-foreground">{permission.description}</div>
                                    </div>
                                    <Select
                                      value={visibility}
                                      onValueChange={(value) => setPermissionVisibility(permission.key, value as PermissionVisibility)}
                                    >
                                      <SelectTrigger 
                                        className="w-[120px]"
                                        data-testid={`visibility-select-${permission.key}`}
                                      >
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {VISIBILITY_OPTIONS.map((option) => (
                                          <SelectItem key={option.value} value={option.value}>
                                            <div className="flex items-center gap-2">
                                              <option.icon className={`h-4 w-4 ${option.color}`} />
                                              {option.label}
                                            </div>
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                );
                              })}
                            </div>
                          </CollapsibleContent>
                        </div>
                      </Collapsible>
                    );
                  })}
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="feature-flags" className="flex-1 overflow-auto mt-4 space-y-4">
              <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                Toggle which entitlements workspaces on this tier receive. Some features depend on
                another being on — enabling a dependent feature turns its prerequisite on, and turning
                a prerequisite off disables its dependents. Runtime enforcement is configured separately.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {FEATURE_FLAGS.map((flag) => {
                  const parentDef = flag.dependsOn
                    ? FEATURE_FLAGS.find((f) => f.key === flag.dependsOn)
                    : undefined;
                  const parentOff = !!flag.dependsOn && !featureFlagsEdit[flag.dependsOn];
                  return (
                    <div
                      key={flag.key}
                      className="flex items-start justify-between gap-3 rounded-md border p-3"
                    >
                      <div className="min-w-0">
                        <Label htmlFor={`flag-${flag.key}`} className="text-sm font-medium">
                          {flag.label}
                        </Label>
                        <p className="text-xs text-muted-foreground mt-0.5">{flag.description}</p>
                        {parentDef && (
                          <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-1">
                            Requires {parentDef.label}
                          </p>
                        )}
                      </div>
                      <Switch
                        id={`flag-${flag.key}`}
                        checked={!!featureFlagsEdit[flag.key]}
                        disabled={parentOff}
                        onCheckedChange={(checked) => setFeatureFlag(flag.key, checked)}
                        data-testid={`switch-flag-${flag.key}`}
                      />
                    </div>
                  );
                })}
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex justify-between items-center gap-2 pt-4 border-t flex-wrap">
            {(() => {
              // Preflight assignment count comes from GET /api/platform/tiers.
              // When > 0 we proactively disable Delete with a tooltip explaining
              // why, since the DELETE route refuses to orphan workspaces anyway.
              const assignedCount = selectedTier?.assignedWorkspaceCount ?? 0;
              const blockedByAssignments = assignedCount > 0;
              const workspaceWord = assignedCount === 1 ? "workspace" : "workspaces";
              const deleteButton = (
                <Button
                  variant="outline"
                  onClick={() => setDeleteConfirmOpen(true)}
                  disabled={deleteTierMutation.isPending || blockedByAssignments}
                  className="border-destructive text-destructive hover:bg-destructive/10"
                  data-testid="button-delete-tier"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete tier
                </Button>
              );
              return (
                <div className="flex items-center gap-3 flex-wrap">
                  {blockedByAssignments ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        {/* Radix Tooltip can't attach to a disabled <button>, so
                            wrap in a span that still receives pointer/focus. */}
                        <span tabIndex={0} className="inline-flex">
                          {deleteButton}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent data-testid="tooltip-delete-tier-blocked">
                        Move the {assignedCount} {workspaceWord} on this tier to another tier first.
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    deleteButton
                  )}
                  <span
                    className={`text-xs ${blockedByAssignments ? "text-amber-700 dark:text-amber-400" : "text-muted-foreground"}`}
                    data-testid="text-tier-assigned-workspaces"
                  >
                    {assignedCount} {workspaceWord} on this tier
                  </span>
                </div>
              );
            })()}
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => selectedTier && duplicateTierMutation.mutate(selectedTier.id)}
                disabled={!selectedTier || duplicateTierMutation.isPending}
                data-testid="button-duplicate-tier-footer"
              >
                <Copy className="h-4 w-4 mr-2" />
                {duplicateTierMutation.isPending ? "Duplicating..." : "Duplicate"}
              </Button>
              <Button
                variant="outline"
                onClick={() => setEditMode(false)}
                data-testid="button-cancel-tier-edit"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={updateTierMutation.isPending}
                data-testid="button-save-tier"
              >
                {updateTierMutation.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <TierPreviewModal
        open={!!previewTier}
        onOpenChange={(o) => { if (!o) setPreviewTier(null); }}
        tier={previewTier}
      />

      <Dialog open={addTierOpen} onOpenChange={setAddTierOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="dialog-add-tier">
          <DialogHeader>
            <DialogTitle>Add Tier</DialogTitle>
            <DialogDescription>
              Create a new permission tier. You can configure limits, permissions, and per-tier UI after it's created.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-tier-name">Slug</Label>
              <Input
                id="new-tier-name"
                value={newTier.name}
                onChange={(e) => setNewTier((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. starter, agency_pro"
                data-testid="input-new-tier-name"
              />
              <p className="text-xs text-muted-foreground">
                Lowercase letters, numbers, and underscores. Used internally and in URLs.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-tier-display">Display Name</Label>
              <Input
                id="new-tier-display"
                value={newTier.displayName}
                onChange={(e) => setNewTier((p) => ({ ...p, displayName: e.target.value }))}
                placeholder="e.g. Starter"
                data-testid="input-new-tier-display"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-tier-desc">Description</Label>
              <Textarea
                id="new-tier-desc"
                value={newTier.description}
                onChange={(e) => setNewTier((p) => ({ ...p, description: e.target.value }))}
                placeholder="Short description"
                rows={2}
                data-testid="input-new-tier-description"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-tier-audience">Audience</Label>
              <Select
                value={newTier.audience}
                onValueChange={(v) =>
                  setNewTier((p) => ({
                    ...p,
                    audience: v as "individual" | "team_enterprise" | "untagged",
                  }))
                }
              >
                <SelectTrigger id="new-tier-audience" data-testid="select-new-tier-audience">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">Individual</SelectItem>
                  <SelectItem value="team_enterprise">Team and Enterprise</SelectItem>
                  <SelectItem value="untagged">Untagged (shows under both)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="new-tier-monthly">Monthly Price ($)</Label>
                <Input
                  id="new-tier-monthly"
                  type="number"
                  step="0.01"
                  value={newTier.monthlyPrice}
                  onChange={(e) => setNewTier((p) => ({ ...p, monthlyPrice: e.target.value }))}
                  placeholder="0.00"
                  data-testid="input-new-tier-monthly"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-tier-yearly">Yearly Price ($)</Label>
                <Input
                  id="new-tier-yearly"
                  type="number"
                  step="0.01"
                  value={newTier.yearlyPrice}
                  onChange={(e) => setNewTier((p) => ({ ...p, yearlyPrice: e.target.value }))}
                  placeholder="0.00"
                  data-testid="input-new-tier-yearly"
                />
              </div>
            </div>
            <div className="border-t pt-4 space-y-4">
              <div>
                <h4 className="text-sm font-semibold">Signup Marketing</h4>
                <p className="text-xs text-muted-foreground">
                  Optional. Toggle <strong>Show on Signup</strong> when ready to make this tier visible on the public Signup page.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="new-tier-icon">Icon</Label>
                  <Select
                    value={newTier.marketingIcon}
                    onValueChange={(v) => setNewTier((p) => ({ ...p, marketingIcon: v }))}
                  >
                    <SelectTrigger id="new-tier-icon" data-testid="select-new-tier-marketing-icon">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MARKETING_ICONS.map((opt) => (
                        <SelectItem key={opt.key} value={opt.key}>
                          <div className="flex items-center gap-2">
                            <opt.icon className="h-4 w-4" />
                            {opt.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-tier-highlight">Highlight Badge</Label>
                  <Input
                    id="new-tier-highlight"
                    value={newTier.marketingHighlight}
                    onChange={(e) => setNewTier((p) => ({ ...p, marketingHighlight: e.target.value }))}
                    placeholder="e.g. Most Popular"
                    maxLength={50}
                    data-testid="input-new-tier-marketing-highlight"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center justify-between rounded-md border p-3">
                  <div>
                    <Label htmlFor="new-tier-popular" className="text-sm font-medium">
                      Popular Styling
                    </Label>
                    <p className="text-xs text-muted-foreground">Lifted/gradient card.</p>
                  </div>
                  <Switch
                    id="new-tier-popular"
                    checked={newTier.marketingPopular}
                    onCheckedChange={(v) => setNewTier((p) => ({ ...p, marketingPopular: v }))}
                    data-testid="switch-new-tier-marketing-popular"
                  />
                </div>
                <div className="flex items-center justify-between rounded-md border p-3">
                  <div>
                    <Label htmlFor="new-tier-show" className="text-sm font-medium">
                      Show on Signup
                    </Label>
                    <p className="text-xs text-muted-foreground">Make this tier visible publicly.</p>
                  </div>
                  <Switch
                    id="new-tier-show"
                    checked={newTier.marketingShowOnSignup}
                    onCheckedChange={(v) => setNewTier((p) => ({ ...p, marketingShowOnSignup: v }))}
                    data-testid="switch-new-tier-marketing-show"
                  />
                </div>
              </div>
              <MarketingFeaturesEditor
                features={newTierFeatures}
                onChange={setNewTierFeatures}
                idPrefix="new"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAddTierOpen(false)}
              data-testid="button-cancel-add-tier"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateTier}
              disabled={createTierMutation.isPending}
              data-testid="button-create-tier"
            >
              {createTierMutation.isPending ? "Creating..." : "Create Tier"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent data-testid="dialog-confirm-delete-tier">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this tier?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the tier <strong>{selectedTier?.displayName || selectedTier?.name}</strong>.
              The action will be blocked if any workspace currently uses this tier.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-tier">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (selectedTier) deleteTierMutation.mutate(selectedTier.id);
              }}
              disabled={deleteTierMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete-tier"
            >
              {deleteTierMutation.isPending ? "Deleting..." : "Delete tier"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default function PlatformTiers() {
  return <PlatformLayout><TiersContent /></PlatformLayout>;
}
